import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/feature/Navbar';
import Footer from '../../../../components/feature/Footer';

// CodeBlock bileşeni
const CodeBlock = ({ title, code }: { title: string; code: string }) => {
  return (
    <div className="bg-gray-900 rounded-xl overflow-hidden">
      <div className="bg-gray-800 px-6 py-3 border-b border-gray-700">
        <h5 className="text-sm font-semibold text-gray-200">{title}</h5>
      </div>
      <div className="p-6 overflow-x-auto">
        <pre className="text-sm text-gray-100 font-mono leading-relaxed">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  );
};

const SoarLogicAppsPage = () => {
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  const [isImage2Expanded, setIsImage2Expanded] = useState(false);
  const [isImage3Expanded, setIsImage3Expanded] = useState(false);
  const [isImage4Expanded, setIsImage4Expanded] = useState(false);
  const [isImage5Expanded, setIsImage5Expanded] = useState(false);
  const [isImage6Expanded, setIsImage6Expanded] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-purple-600 to-blue-600 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
            }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/sentinel"
              className="inline-flex items-center space-x-2 text-white/80 hover:text-white text-sm mb-6 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Sentinel'e Dön</span>
            </Link>
            <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg mb-6">
              <span className="text-white text-sm font-medium">Otomasyon</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
              Microsoft Sentinel SOAR :&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; Olay Müdahale Süreçlerini Otomatize Etmek - Bölüm 1
            </h1>
            <div className="flex items-center space-x-6 text-white/80 text-sm">
              <span className="flex items-center space-x-2">
                <i className="ri-calendar-line"></i>
                <span>3 Aralık 2025</span>
              </span>
              <span className="flex items-center space-x-2">
                <i className="ri-time-line"></i>
                <span>18 dk okuma</span>
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Makale İçeriği */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="prose prose-lg max-w-none"
          >
            {/* Öne Çıkan Görsel */}
            <div className="w-full h-96 rounded-2xl overflow-hidden mb-12 shadow-xl">
              <img
                src="https://public.readdy.ai/ai/img_res/bb70941ef5f25efca2eabffcf0b3e7cc.jpg"
                alt="Microsoft Sentinel SOAR ve Logic Apps"
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* Giriş */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-l-4 border-purple-600 p-6 rounded-lg mb-10">
              <p className="text-gray-700 leading-relaxed text-base m-0">
                <strong>Microsoft Sentinel</strong>, modern güvenlik operasyon merkezlerinin (SOC) en büyük zorluklarından birini çözmek için tasarlanmış güçlü bir <strong>SOAR (Security Orchestration, Automation and Response)</strong> platformudur. Bu makalede, Sentinel'in otomasyon yeteneklerini derinlemesine inceleyecek, <strong>Automation Rules</strong> ve <strong>Logic Apps</strong> entegrasyonu ile güvenlik olaylarına nasıl otomatik yanıt verebileceğinizi öğreneceksiniz.
              </p>
            </div>

            {/* SOAR Nedir? */}
            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <i className="ri-robot-line text-purple-600 text-xl"></i>
              </div>
              <span>SOAR Nedir ve Neden Önemlidir?</span>
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>SOAR (Security Orchestration, Automation and Response)</strong>, güvenlik ekiplerinin tekrarlayan görevleri otomatikleştirmesini, farklı güvenlik araçlarını entegre etmesini ve tehditlere daha hızlı yanıt vermesini sağlayan bir teknolojidir.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Geleneksel güvenlik operasyonlarında, bir güvenlik olayı tespit edildiğinde analistler manuel olarak:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 ml-4">
              <li>Olayı incelemek için farklı sistemlere giriş yapar</li>
              <li>Kullanıcı hesaplarını kontrol eder</li>
              <li>IP adreslerini araştırır</li>
              <li>Tehdit istihbaratı kaynaklarını sorgular</li>
              <li>Gerekirse hesapları devre dışı bırakır veya cihazları izole eder</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-6">
              Bu süreç hem zaman alıcıdır hem de insan hatasına açıktır. <strong>SOAR</strong>, bu adımların çoğunu otomatikleştirerek SOC ekiplerinin daha stratejik görevlere odaklanmasını sağlar.
            </p>

            {/* Sentinel SOAR Mimarisi */}
            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <i className="ri-flow-chart text-blue-600 text-xl"></i>
              </div>
              <span>Logic Apps (Playbooks)</span>
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Logic Apps</strong>, Azure'un sunduğu düşük kodlu/kodsuz entegrasyon platformudur. Sentinel'de "Playbook" olarak adlandırılan Logic Apps, karmaşık otomasyon senaryolarını gerçekleştirmek için kullanılır.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Logic Apps ile Yapabilecekleriniz:</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 ml-4">
              <li><strong>Zenginleştirme:</strong> IP adreslerini, domain'leri veya dosya hash'lerini tehdit istihbaratı kaynaklarıyla sorgulayın</li>
              <li><strong>Bildirim:</strong> Teams, Slack veya e-posta ile güvenlik ekibini bilgilendirin</li>
              <li><strong>İzolasyon:</strong> Şüpheli cihazları ağdan otomatik olarak izole edin</li>
              <li><strong>Hesap Yönetimi:</strong> Tehlikeye girmiş kullanıcı hesaplarını devre dışı bırakın veya parolalarını sıfırlayın</li>
              <li><strong>Ticket Oluşturma:</strong> ServiceNow, Jira gibi ITSM sistemlerinde otomatik ticket açın</li>
            </ul>

            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-blue-600 p-6 rounded-lg my-8">
              <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center space-x-2">
                <i className="ri-rocket-2-line text-blue-600"></i>
                <span>Örnek Playbook: Şüpheli Oturum Açma Müdahalesi</span>
              </h4>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                Sentinel, imkansız seyahat (impossible travel) tespit ettiğinde otomatik olarak:
              </p>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 text-sm ml-4">
                <li>Kullanıcının son 24 saatteki tüm oturum açma aktivitelerini sorgular</li>
                <li>IP adresini VirusTotal ve AbuseIPDB ile kontrol eder</li>
                <li>Kullanıcının yöneticisine Microsoft Teams'de bildirim gönderir</li>
                <li>Kullanıcı hesabını geçici olarak devre dışı bırakır</li>
                <li>ServiceNow'da bir güvenlik ticket'ı oluşturur</li>
                <li>Sentinel'de olayı "Müdahale Edildi" olarak etiketler</li>
              </ol>
            </div>

            {/* KQL Sorguları ile Otomasyonu Akıllandırmak */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">KQL Sorguları ile Otomasyonu Akıllandırmak</h2>
              <p className="mb-6">
                Sentinel'de bir otomasyonun başarısı, tetikleyici sorgunun ne kadar spesifik olduğuna bağlıdır. Logic App'e sadece "bir olay oldu" demek yerine, "şu kullanıcı, şu IP'den, şu kadar başarısız deneme yaptı" verisini rafine ederek göndermek gerekir.
              </p>

              <div className="bg-gradient-to-br from-[#0078D4]/5 to-[#005A9E]/5 rounded-2xl p-8 mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Örnek: Başarısız Oturum Açma Tespiti</h3>
                <p className="text-gray-700 mb-4">
                  Aşağıdaki KQL sorgusu, son 5 dakikada 10'dan fazla başarısız oturum açma denemesi yapan kullanıcıları tespit eder ve Logic App'e detaylı veri gönderir:
                </p>

                <CodeBlock
                  title="KQL - Akıllı Başarısız Oturum Açma Tespiti"
                  code={`SigninLogs
| where TimeGenerated > ago(5m)
| where ResultType == "50126"  // Hatalı kullanıcı adı veya şifre
| summarize DenemeSayisi = count(), 
            HedefUygulama = make_set(AppDisplayName), 
            IPAdresi = any(IPAddress) 
            by UserPrincipalName
| where DenemeSayisi > 10`}
                />

                <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-6">
                  <div className="flex items-start">
                    <i className="ri-lightbulb-line text-2xl text-blue-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Logic App Entegrasyonu</h4>
                      <p className="text-gray-700 text-sm mb-3">
                        Bu sorguyu bir Analytics Rule'da kullandığınızda, Logic App'e şu veriler gönderilir:
                      </p>
                      <ul className="text-gray-700 text-sm space-y-1">
                        <li className="flex items-start">
                          <i className="ri-check-line text-blue-600 mr-2 mt-0.5"></i>
                          <span><strong>UserPrincipalName:</strong> Saldırı hedefi kullanıcı</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-check-line text-blue-600 mr-2 mt-0.5"></i>
                          <span><strong>DenemeSayisi:</strong> Kaç kez deneme yapıldı (örn: 15)</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-check-line text-blue-600 mr-2 mt-0.5"></i>
                          <span><strong>HedefUygulama:</strong> Hangi uygulamalara erişim denendi (örn: ["Office 365", "Azure Portal"])</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-check-line text-blue-600 mr-2 mt-0.5"></i>
                          <span><strong>IPAdresi:</strong> Saldırganın IP adresi (örn: 203.0.113.45)</span>
                        </li>
                      </ul>
                      <p className="text-gray-700 text-sm mt-3">
                        Logic App bu verileri kullanarak otomatik olarak: kullanıcıyı devre dışı bırakabilir, IP'yi engelleyebilir, SOC ekibine detaylı bildirim gönderebilir.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Analytics Rule Oluşturma Adımları */}
              <div className="mt-8 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-100">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <i className="ri-alarm-warning-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Bu Sorgu İçin Analytics Rule Nasıl Oluşturulur?</h3>
                    <p className="text-gray-700 text-sm">
                      Yukarıdaki KQL sorgusunu kullanarak otomatik alarm oluşturmak için aşağıdaki adımları izleyin:
                    </p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div className="flex items-start space-x-4">
                    <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold flex-shrink-0 text-sm">
                      1
                    </div>
                    <div className="pt-1">
                      <h4 className="font-bold text-gray-900 mb-1">Analytics Sayfasına Gidin</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Microsoft Sentinel &gt; <strong>Analytics</strong> &gt; <strong>+ Create</strong> &gt; <strong>Scheduled query rule</strong> seçin.
                      </p>
                      <div className="mt-3">
                        <p className="text-gray-500 text-xs mb-2 flex items-center space-x-2">
                          <i className="ri-image-line text-gray-400"></i>
                          <span>Büyütmek için tıklayın</span>
                        </p>
                        <div
                          className="relative group cursor-pointer rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
                          onClick={() => setIsImage3Expanded(true)}
                        >
                          <img
                            src="https://static.readdy.ai/image/f3543b778985655566d8e7a649090a0e/a4405dbb53599c786197dbca2ad7a5d6.png"
                            alt="Microsoft Sentinel Analytics - Scheduled query rule oluşturma"
                            className="w-full h-auto object-contain"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300 flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <i className="ri-zoom-in-line text-xl text-gray-700"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold flex-shrink-0 text-sm">
                      2
                    </div>
                    <div className="pt-1">
                      <h4 className="font-bold text-gray-900 mb-1">Genel Ayarları Yapın (General)</h4>
                      <ul className="text-gray-700 text-sm space-y-1 mt-2">
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-indigo-600 mr-1 mt-0.5 flex-shrink-0"></i>
                          <span><strong>Name:</strong> "Başarısız Oturum Açma - Brute Force Tespiti"</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-indigo-600 mr-1 mt-0.5 flex-shrink-0"></i>
                          <span><strong>Description:</strong> "5 dakikada 10'dan fazla başarısız oturum açma denemesi tespit edildiğinde alarm oluşturur"</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-indigo-600 mr-1 mt-0.5 flex-shrink-0"></i>
                          <span><strong>Severity:</strong> High (Yüksek)</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-indigo-600 mr-1 mt-0.5 flex-shrink-0"></i>
                          <span><strong>MITRE ATT&CK:</strong> Credential Access - Brute Force (T1110)</span>
                        </li>
                      </ul>
                      <div className="mt-3">
                        <p className="text-gray-500 text-xs mb-2 flex items-center space-x-2">
                          <i className="ri-image-line text-gray-400"></i>
                          <span>Büyütmek için tıklayın</span>
                        </p>
                        <div
                          className="relative group cursor-pointer rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
                          onClick={() => setIsImage4Expanded(true)}
                        >
                          <img
                            src="https://static.readdy.ai/image/f3543b778985655566d8e7a649090a0e/565c66fff1d5383b2c26e2af38114aef.png"
                            alt="Analytics Rule - General ayarları ve MITRE ATT&CK seçimi"
                            className="w-full h-auto object-contain"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300 flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <i className="ri-zoom-in-line text-xl text-gray-700"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold flex-shrink-0 text-sm">
                      3
                    </div>
                    <div className="pt-1">
                      <h4 className="font-bold text-gray-900 mb-1">KQL Sorgusunu Girin (Set rule logic)</h4>
                      <p className="text-gray-700 text-sm leading-relaxed mb-2">
                        <strong>Rule query</strong> alanına yukarıdaki KQL sorgusunu yapıştırın. Ardından:
                      </p>
                      <ul className="text-gray-700 text-sm space-y-1">
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-indigo-600 mr-1 mt-0.5 flex-shrink-0"></i>
                          <span><strong>Query scheduling:</strong> Her 5 dakikada bir çalıştır (Run query every: 5 minutes)</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-indigo-600 mr-1 mt-0.5 flex-shrink-0"></i>
                          <span><strong>Lookup data:</strong> Son 5 dakikalık veriyi kontrol et (Lookup data from the last: 5 minutes)</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-indigo-600 mr-1 mt-0.5 flex-shrink-0"></i>
                          <span><strong>Alert threshold:</strong> Sonuç sayısı 0'dan büyükse alarm oluştur (Greater than: 0)</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold flex-shrink-0 text-sm">
                      4
                    </div>
                    <div className="pt-1">
                      <h4 className="font-bold text-gray-900 mb-1">Entity Mapping Yapın</h4>
                      <p className="text-gray-700 text-sm leading-relaxed mb-2">
                        <strong>Entity mapping</strong> bölümünde şu eşleştirmeleri yapın:
                      </p>
                      <ul className="text-gray-700 text-sm space-y-1">
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-indigo-600 mr-1 mt-0.5 flex-shrink-0"></i>
                          <span><strong>Entity type:</strong> Account → <strong>Identifier:</strong> UserPrincipalName → <strong>Value:</strong> UserPrincipalName</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-indigo-600 mr-1 mt-0.5 flex-shrink-0"></i>
                          <span><strong>Entity type:</strong> IP → <strong>Identifier:</strong> Address → <strong>Value:</strong> IPAdresi</span>
                        </li>
                      </ul>
                      <p className="text-gray-700 text-sm mt-2 italic">
                        Bu eşleştirme, Sentinel'in kullanıcı ve IP bilgilerini otomatik olarak zenginleştirmesini sağlar.
                      </p>
                      <div className="mt-3">
                        <p className="text-gray-500 text-xs mb-2 flex items-center space-x-2">
                          <i className="ri-image-line text-gray-400"></i>
                          <span>Büyütmek için tıklayın</span>
                        </p>
                        <div
                          className="relative group cursor-pointer rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
                          onClick={() => setIsImage5Expanded(true)}
                        >
                          <img
                            src="https://static.readdy.ai/image/f3543b778985655566d8e7a649090a0e/eaa32ae25894e446c1616f66a4644db3.png"
                            alt="Analytics Rule - Entity Mapping ve Query Scheduling ayarları"
                            className="w-full h-auto object-contain"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300 flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <i className="ri-zoom-in-line text-xl text-gray-700"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold flex-shrink-0 text-sm">
                      5
                    </div>
                    <div className="pt-1">
                      <h4 className="font-bold text-gray-900 mb-1">Automated Response Ekleyin (İsteğe Bağlı)</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        <strong>Automated response</strong> sekmesinde, daha önce oluşturduğunuz Logic App (Playbook) varsa buradan ekleyebilirsiniz. Örneğin: "Kullanıcıyı Devre Dışı Bırak" veya "SOC Ekibine Bildirim Gönder" playbook'larını tetikleyin.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold flex-shrink-0 text-sm">
                      6
                    </div>
                    <div className="pt-1">
                      <h4 className="font-bold text-gray-900 mb-1">Kuralı Oluşturun</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        <strong>Review and create</strong> sekmesinde tüm ayarları kontrol edin ve <strong>Create</strong> butonuna tıklayın. Kural artık aktif ve her 5 dakikada bir çalışacak.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 bg-white border border-indigo-200 rounded-xl p-5">
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-lightbulb-flash-line text-indigo-600 text-base"></i>
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 mb-1 text-sm">Pro İpucu: Test Etmeyi Unutmayın</h5>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Kuralı oluşturduktan sonra, <strong>Analytics &gt; Active rules</strong> bölümünden kuralınızı bulun ve <strong>"Run query"</strong> butonuna tıklayarak test edin. Eğer sorgu sonuç döndürüyorsa, kural doğru çalışıyor demektir.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-6">
                <div className="flex items-start">
                  <i className="ri-alert-line text-2xl text-amber-600 mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Eşik Değer Optimizasyonu</h4>
                    <p className="text-gray-700 text-sm">
                      <strong>DenemeSayisi &gt; 10</strong> değeri, organizasyonunuza göre ayarlanmalıdır. Çok düşük eşik (örn: 3) çok fazla false positive üretir, çok yüksek eşik (örn: 50) gerçek saldırıları kaçırabilir. Başlangıç için 10-15 arası önerilir, ardından 2-4 haftalık veri analizi ile optimize edin.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 mt-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Automation Rules ile Neler Yapılabilir?</h3>
                <p className="text-gray-700 mb-6">
                  Automation Rules, Sentinel'in kendi içindeki "otomatik pilotu"dur. Logic App yazmadan önce şu temel işlemleri saniyeler içinde halledebilirsiniz:
                </p>
                <p className="text-gray-700 mb-6">
                  Logic Apps ile yapabilecekleriniz:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                      <i className="ri-user-add-line text-2xl text-purple-600"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Olayı Sahiplendirme (Assigning)</h4>
                    <p className="text-gray-700 text-sm">
                      Alarm oluşur oluşmaz ilgili analiste atama.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                      <i className="ri-price-tag-3-line text-2xl text-blue-600"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Etiketleme (Tagging)</h4>
                    <p className="text-gray-700 text-sm">
                      Olayın türüne göre (Örn: "Brute Force", "Kritik Sunucu") etiket ekleme.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center mb-4">
                      <i className="ri-refresh-line text-2xl text-green-600"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Durum ve Öncelik Güncelleme</h4>
                    <p className="text-gray-700 text-sm">
                      Otomatik olarak durumu "Active"e çekme veya önem derecesini (Severity) yükseltme.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center mb-4">
                      <i className="ri-play-circle-line text-2xl text-orange-600"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Playbook Tetikleme</h4>
                    <p className="text-gray-700 text-sm">
                      Hazırlanan Logic App'i çalıştırma.
                    </p>
                  </div>
                </div>
              </div>

              {/* Kritik Ön Hazırlık */}
              <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-8">
                <p className="text-gray-700 mb-6">
                  Sentinel&apos;de bir olay (Incident) oluştuğunda, bu olayı karşılayan ilk katman Automation Rule&apos;dur. Ancak kuruluma geçmeden önce, sistemin arka planda çalışabilmesi için hayati bir &quot;yetkilendirme&quot; adımını tamamlamış olmanız gerekir.
                </p>

                <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-6 mb-8">
                  <div className="flex items-start">
                    <i className="ri-error-warning-line text-2xl text-red-600 mr-4 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Kritik Ön Hazırlık: Otomasyon İzinlerini Tanımlamak</h4>
                      <p className="text-gray-700 text-sm">
                        Eğer Azure portalınızda ilk kez bir otomasyon kuralı oluşturuyorsanız, Sentinel&apos;in sizin adınıza Playbook (Logic App) çalıştırabilmesi için açıkça yetkilendirilmesi gerekir. Bu izin verilmediği sürece oluşturduğunuz kurallar hata verecektir.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-9 h-9 rounded-full bg-gray-900 text-white flex items-center justify-center font-bold flex-shrink-0 text-sm">
                      1
                    </div>
                    <div className="pt-1">
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Sentinel menüsünde <strong>Settings (Ayarlar)</strong> sekmesine gidin.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-9 h-9 rounded-full bg-gray-900 text-white flex items-center justify-center font-bold flex-shrink-0 text-sm">
                      2
                    </div>
                    <div className="pt-1">
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Üstteki sekmelerden <strong>Playbook Permissions (Playbook İzinleri)</strong> kısmına tıklayın.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-9 h-9 rounded-full bg-gray-900 text-white flex items-center justify-center font-bold flex-shrink-0 text-sm">
                      3
                    </div>
                    <div className="pt-1">
                      <p className="text-gray-700 text-sm leading-relaxed">
                        <strong>Configure Permissions</strong> butonuna basarak, Playbook&apos;larınızın bulunduğu <strong>Resource Group&apos;u (Kaynak Grubu)</strong> seçin ve <strong>Apply</strong> deyin.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 bg-gray-50 border border-gray-200 rounded-xl p-5">
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-information-line text-gray-500 text-base"></i>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      <strong>Not:</strong> Bu işlem, Microsoft Sentinel&apos;e o kaynak grubundaki Logic App&apos;leri &quot;okuma ve çalıştırma&quot; yetkisi (<strong>Sentinel Automation Contributor</strong> rolü) verir.
                    </p>
                  </div>
                </div>

                {/* Ekran Görüntüsü */}
                <div className="mt-8">
                  <p className="text-gray-500 text-sm mb-3 flex items-center space-x-2">
                    <i className="ri-image-line text-gray-400"></i>
                    <span>Sentinel Automation sayfası — büyütmek için tıklayın</span>
                  </p>
                  <div
                    className="relative group cursor-pointer rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
                    onClick={() => setIsImageExpanded(true)}
                  >
                    <img
                      src="https://static.readdy.ai/image/f3543b778985655566d8e7a649090a0e/eb24182434e73819b89b7a61479540be.png"
                      alt="Microsoft Sentinel Automation sayfası - Configure Permissions"
                      className="w-full h-auto object-contain"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <i className="ri-zoom-in-line text-xl text-gray-700"></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* İkinci Ekran Görüntüsü */}
                <div className="mt-6">
                  <p className="text-gray-500 text-sm mb-3 flex items-center space-x-2">
                    <i className="ri-image-line text-gray-400"></i>
                    <span>Sentinel Automation — büyütmek için tıklayın</span>
                  </p>
                  <div
                    className="relative group cursor-pointer rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
                    onClick={() => setIsImage2Expanded(true)}
                  >
                    <img
                      src="https://static.readdy.ai/image/f3543b778985655566d8e7a649090a0e/976db09d4f15fa51e517b2e28afd5a89.png"
                      alt="Microsoft Sentinel Automation sayfası - ikinci görünüm"
                      className="w-full h-auto object-contain"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <i className="ri-zoom-in-line text-xl text-gray-700"></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lightbox Modal - İlk Görsel */}
                {isImageExpanded && (
                  <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 cursor-pointer"
                    onClick={() => setIsImageExpanded(false)}
                  >
                    <div className="relative max-w-6xl w-full">
                      <button
                        onClick={() => setIsImageExpanded(false)}
                        className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-white text-2xl"></i>
                      </button>
                      <img
                        src="https://static.readdy.ai/image/f3543b778985655566d8e7a649090a0e/eb24182434e73819b89b7a61479540be.png"
                        alt="Microsoft Sentinel Automation sayfası - Configure Permissions"
                        className="w-full h-auto rounded-xl shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <p className="text-white/70 text-sm text-center mt-4">Kapatmak için herhangi bir yere tıklayın</p>
                    </div>
                  </div>
                )}

                {/* Lightbox Modal - İkinci Görsel */}
                {isImage2Expanded && (
                  <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 cursor-pointer"
                    onClick={() => setIsImage2Expanded(false)}
                  >
                    <div className="relative max-w-6xl w-full">
                      <button
                        onClick={() => setIsImage2Expanded(false)}
                        className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-white text-2xl"></i>
                      </button>
                      <img
                        src="https://static.readdy.ai/image/f3543b778985655566d8e7a649090a0e/976db09d4f15fa51e517b2e28afd5a89.png"
                        alt="Microsoft Sentinel Automation sayfası - ikinci görünüm"
                        className="w-full h-auto rounded-xl shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <p className="text-white/70 text-sm text-center mt-4">Kapatmak için herhangi bir yere tıklayın</p>
                    </div>
                  </div>
                )}

                {/* Lightbox Modal - Üçüncü Görsel (Analytics Rule) */}
                {isImage3Expanded && (
                  <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 cursor-pointer"
                    onClick={() => setIsImage3Expanded(false)}
                  >
                    <div className="relative max-w-6xl w-full">
                      <button
                        onClick={() => setIsImage3Expanded(false)}
                        className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-white text-2xl"></i>
                      </button>
                      <img
                        src="https://static.readdy.ai/image/f3543b778985655566d8e7a649090a0e/a4405dbb53599c786197dbca2ad7a5d6.png"
                        alt="Microsoft Sentinel Analytics - Scheduled query rule oluşturma"
                        className="w-full h-auto rounded-xl shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <p className="text-white/70 text-sm text-center mt-4">Kapatmak için herhangi bir yere tıklayın</p>
                    </div>
                  </div>
                )}

                {/* Lightbox Modal - Dördüncü Görsel (General Ayarları) */}
                {isImage4Expanded && (
                  <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 cursor-pointer"
                    onClick={() => setIsImage4Expanded(false)}
                  >
                    <div className="relative max-w-6xl w-full">
                      <button
                        onClick={() => setIsImage4Expanded(false)}
                        className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-white text-2xl"></i>
                      </button>
                      <img
                        src="https://static.readdy.ai/image/f3543b778985655566d8e7a649090a0e/565c66fff1d5383b2c26e2af38114aef.png"
                        alt="Analytics Rule - General ayarları ve MITRE ATT&CK seçimi"
                        className="w-full h-auto rounded-xl shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <p className="text-white/70 text-sm text-center mt-4">Kapatmak için herhangi bir yere tıklayın</p>
                    </div>
                  </div>
                )}

                {/* Lightbox Modal - Beşinci Görsel (Entity Mapping) */}
                {isImage5Expanded && (
                  <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 cursor-pointer"
                    onClick={() => setIsImage5Expanded(false)}
                  >
                    <div className="relative max-w-6xl w-full">
                      <button
                        onClick={() => setIsImage5Expanded(false)}
                        className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-white text-2xl"></i>
                      </button>
                      <img
                        src="https://static.readdy.ai/image/f3543b778985655566d8e7a649090a0e/eaa32ae25894e446c1616f66a4644db3.png"
                        alt="Analytics Rule - Entity Mapping ve Query Scheduling ayarları"
                        className="w-full h-auto rounded-xl shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <p className="text-white/70 text-sm text-center mt-4">Kapatmak için herhangi bir yere tıklayın</p>
                    </div>
                  </div>
                )}

                {/* Lightbox Modal - Altıncı Görsel (Automation Rule Actions) */}
                {isImage6Expanded && (
                  <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 cursor-pointer"
                    onClick={() => setIsImage6Expanded(false)}
                  >
                    <div className="relative max-w-6xl w-full">
                      <button
                        onClick={() => setIsImage6Expanded(false)}
                        className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-white text-2xl"></i>
                      </button>
                      <img
                        src="https://static.readdy.ai/image/f3543b778985655566d8e7a649090a0e/7a6821ebd1be1e5e91f306ca0a34f455.png"
                        alt="Automation Rule - Actions ve Tags ayarları"
                        className="w-full h-auto rounded-xl shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <p className="text-white/70 text-sm text-center mt-4">Kapatmak için herhangi bir yere tıklayın</p>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Playbook Oluşturma */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Adım Adım Automation Rule Oluşturma</h2>
              <p className="text-gray-700 mb-6">
                İzinleri tanımladıktan sonra artık kuralımızı yazabiliriz:
              </p>

              <div className="space-y-6 my-8">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Giriş</h4>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Microsoft Sentinel panelinde sol menüden <strong>Automation</strong> sekmesine gidin.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Kural Oluşturma</h4>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Üst menüden <strong>Create &gt; Automation rule</strong> seçeneğine tıklayın.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Koşulları Belirleme (Conditions)</h4>
                    <ul className="text-gray-700 text-sm space-y-2 mt-2">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-purple-600 mr-1 mt-0.5 flex-shrink-0"></i>
                        <span><strong>If Analytics rule name:</strong> Burada "Bölüm 1"de hazırladığımız KQL alarmının adını seçin.</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-purple-600 mr-1 mt-0.5 flex-shrink-0"></i>
                        <span>İsteğe bağlı olarak; <strong>Severity equals High</strong> gibi ek filtreler ekleyerek sadece kritik saldırılarda çalışmasını sağlayabilirsiniz.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Eylemleri Belirleme (Actions)</h4>
                    <ul className="text-gray-700 text-sm space-y-2 mt-2">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-purple-600 mr-1 mt-0.5 flex-shrink-0"></i>
                        <span><strong>Change status:</strong> Olayı otomatik olarak "Active" durumuna çekin.</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-purple-600 mr-1 mt-0.5 flex-shrink-0"></i>
                        <span><strong>Assign owner:</strong> Olayı otomatik olarak kendinize veya ekibinizdeki bir analiste atayın.</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-purple-600 mr-1 mt-0.5 flex-shrink-0"></i>
                        <span><strong>Add tags:</strong> "Automated-Response" veya "Bölüm2-Test" gibi etiketler ekleyerek takibi kolaylaştırın.</span>
                      </li>
                    </ul>
                    <div className="mt-3">
                      <p className="text-gray-500 text-xs mb-2 flex items-center space-x-2">
                        <i className="ri-image-line text-gray-400"></i>
                        <span>Büyütmek için tıklayın</span>
                      </p>
                      <div
                        className="relative group cursor-pointer rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
                        onClick={() => setIsImage6Expanded(true)}
                      >
                        <img
                          src="https://static.readdy.ai/image/f3543b778985655566d8e7a649090a0e/7a6821ebd1be1e5e91f306ca0a34f455.png"
                          alt="Automation Rule - Actions ve Tags ayarları"
                          className="w-full h-auto object-contain"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <i className="ri-zoom-in-line text-xl text-gray-700"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Best Practices */}
            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <i className="ri-shield-check-line text-green-600 text-xl"></i>
              </div>
              <span>SOAR Best Practices</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mb-4">
                  <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">Küçük Başlayın</h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  İlk olarak basit, düşük riskli senaryoları otomatikleştirin. Örneğin, olay etiketleme veya bildirim gönderme.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                  <i className="ri-test-tube-line text-blue-600 text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">Test Edin</h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Playbook'ları production ortamında kullanmadan önce test ortamında kapsamlı testler yapın.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center mb-4">
                  <i className="ri-eye-line text-yellow-600 text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">İzleyin ve Optimize Edin</h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Playbook çalıştırma loglarını düzenli olarak inceleyin. Hata oranlarını ve çalışma sürelerini takip edin.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center mb-4">
                  <i className="ri-shield-user-line text-red-600 text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">İnsan Onayı Ekleyin</h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Kritik eylemlerde (hesap devre dışı bırakma, cihaz izolasyonu) insan onayı adımı ekleyin.
                </p>
              </div>
            </div>

            {/* Sonuç */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-l-4 border-purple-600 p-6 rounded-lg mt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Sonuç</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Microsoft Sentinel'in SOAR yetenekleri, güvenlik ekiplerinin verimliliğini önemli ölçüde artırır. <strong>Automation Rules</strong> ile basit görevleri otomatikleştirerek, <strong>Logic Apps</strong> ile karmaşık müdahale senaryolarını orkestre ederek, SOC ekipleri daha stratejik tehditlere odaklanabilir.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Otomasyon, modern güvenlik operasyonlarının vazgeçilmez bir parçasıdır. Doğru yapılandırıldığında, tehdit müdahale sürenizi dakikalardan saniyelere indirebilir ve insan hatasını minimize edebilirsiniz.
              </p>
            </div>

            {/* Bölüm 2'ye Bağlantı */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-l-4 border-blue-600 p-8 rounded-lg mt-12">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-right-circle-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Sırada Ne Var?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Buraya kadar olan kısımda Sentinel'in kendi iç işleyişini nasıl otomatize edeceğimizi gördük. Ancak asıl güç, bu kuralın tetikleyeceği ve dış sistemlerle (Teams, Firewall, E-posta) konuşan Logic Apps tarafında gizli. Bir sonraki bölümde, KQL verilerini görselleştirip dünyayla paylaşan devasa bir otomasyonu adım adım kuracağız.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </article>

      {/* Diğer Makaleler */}
      <section className="py-16 bg-[#FAFBFC]">
        <div className="max-w-6xl mx-auto px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Diğer Sentinel Makaleleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Link
              to="/sentinel/sentinel-maliyet-optimizasyonu"
              className="group block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100"
            >
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-white text-sm"></i>
                  </div>
                  <span className="text-xs font-semibold text-teal-600">Maliyet Optimizasyonu</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0078D4] transition-colors">
                  Microsoft Sentinel Maliyetinizi Nasıl %40 Düşürürsünüz?
                </h3>
                <p className="text-sm text-gray-500">
                  Sentinel maliyet optimizasyonu stratejileri ve Commitment Tier kullanımı.
                </p>
              </div>
            </Link>

            <Link
              to="/sentinel/sentinel-kurulum-rehberi"
              className="group block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100"
            >
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center">
                    <i className="ri-rocket-2-line text-white text-sm"></i>
                  </div>
                  <span className="text-xs font-semibold text-indigo-600">Kurulum Rehberi</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0078D4] transition-colors">
                  Microsoft Sentinel Kurulum Rehberi: İlk 24 Saatte Atılması Gereken Adımlar
                </h3>
                <p className="text-sm text-gray-500">
                  Sentinel kurulumunun ilk 24 saatinde yapılması gereken kritik adımlar.
                </p>
              </div>
            </Link>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/sentinel"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-[#0078D4] hover:bg-[#005A9E] text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Tüm Sentinel Makalelerine Dön</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SoarLogicAppsPage;
